import React, { useRef, useEffect } from "react";
import { Mic, MicOff, Send } from "lucide-react";

/**
 * Claude v2.4-inspired input bar — pill-shaped, auto-growing textarea,
 * round mic button, square send button. All wiring stays in the parent.
 */
export default function ChatInput({
  value,
  onChange,
  onSend,
  onToggleVoice,
  listening,
  sending,
  onOpenLead,
  onOpenTranscript,
  disclaimer,
  footerNote,
  demoBadge,
  samplePrompts = [],
  onSamplePrompt,
}) {
  const ref = useRef(null);

  // Auto-grow up to max-height (CSS clamps at 110px).
  useEffect(() => {
    const t = ref.current;
    if (!t) return;
    t.style.height = "auto";
    t.style.height = Math.min(t.scrollHeight, 110) + "px";
  }, [value]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div
      className="rch-input-area"
      // Step 4 / Part A — subtle opacity drop while the
      // response/TTS/modal cycle is busy. Driven by the parent's `sending`
      // prop (which now also tracks the isProcessing guard).
      style={sending ? { opacity: 0.6, transition: "opacity 0.15s ease" } : undefined}
    >
      <div className="rch-input-wrap">
        {samplePrompts.length > 0 && (
          <div className="rch-sample-chips" data-testid="rch-sample-chips" style={{
            display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8,
          }}>
            {samplePrompts.map((q) => (
              <button
                key={q}
                type="button"
                disabled={sending}
                onClick={() => onSamplePrompt && onSamplePrompt(q)}
                className="rch-sample-chip"
                data-testid={`rch-sample-chip-${q.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
                style={{
                  fontSize: 12,
                  padding: "5px 11px",
                  borderRadius: 999,
                  background: "rgba(0,0,0,0.04)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  color: "var(--rch-fg, #1a1a1a)",
                  cursor: sending ? "not-allowed" : "pointer",
                  opacity: sending ? 0.5 : 1,
                  transition: "background 0.15s ease, transform 0.1s ease",
                  lineHeight: 1.2,
                }}
                onMouseEnter={(e) => { if (!sending) e.currentTarget.style.background = "rgba(0,0,0,0.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.04)"; }}
              >
                {q}
              </button>
            ))}
          </div>
        )}
        <div className="rch-input-bar">
          <button
            className={`rch-mic-btn ${listening ? "on" : ""}`}
            onClick={onToggleVoice}
            aria-label={listening ? "Stop voice input" : "Start voice input"}
            data-testid="voice-btn"
          >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKey}
            placeholder="Type a message…"
            rows={1}
            // Step 4 / Part A — input disabled while the isProcessing
            // guard is active (parent passes the combined flag via `sending`).
            disabled={sending}
            data-testid="chat-input"
          />
          <button
            className="rch-send-btn"
            onClick={onSend}
            disabled={sending || !value.trim()}
            aria-label="Send message"
            data-testid="send-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="rch-input-foot">
          <button onClick={onOpenLead} data-testid="open-lead">📝 Get in touch</button>
          <button onClick={onOpenTranscript} data-testid="open-transcript">📧 Email transcript</button>
        </div>
        {disclaimer && <p className="rch-disclaimer">{disclaimer}</p>}
        {footerNote && <p className="rch-disclaimer" data-testid="chat-footer-note" style={{ marginTop: 2, fontSize: 10 }}>{footerNote}</p>}
        {demoBadge}
      </div>
    </div>
  );
}
